/**
 * `LienAide` — la porte vers la documentation, depuis l'écran où l'on est
 * bloqué, et vers le PARAGRAPHE qui répond.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CE QU'IL RÉSOUT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La documentation vit sur `/aide` (39 pages, six publics). Sans ce lien, elle
 * n'existe que pour qui pense à la chercher — c'est-à-dire pour personne un
 * soir de première. La commande était explicite : « des liens depuis
 * l'administration / accès employé qui font référence à la documentation pour
 * obtenir de l'aide ».
 *
 * ★ **Il pointe une RÉFÉRENCE, pas une page.** Une référence, c'est une ancre
 *   de page suivie d'un fragment de section : `aide/guichet/vendre#derogation`.
 *   La différence n'est pas cosmétique. Les pages font aujourd'hui six écrans
 *   de long ; « le haut de la page » et « la réponse » n'y sont pas au même
 *   endroit, et quelqu'un qui a une file d'attente derrière lui ne défile pas.
 *   Le fragment reste FACULTATIF : sur un écran dont la page entière parle,
 *   viser une section arbitraire ferait sauter l'introduction pour rien.
 *
 * ★★ **Une référence morte est PIRE qu'un lien vers le sommaire**, parce
 *   qu'elle promet une réponse précise et dépose le lecteur nulle part. C'est
 *   pour cela que `navigation-aide.test.ts` relit toutes les sources du
 *   frontend, ramasse chaque référence écrite dans un écran, et refuse celles
 *   dont la page — ou la SECTION — n'existe pas.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TROIS CHOIX QUI MÉRITENT D'ÊTRE DITS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * **1. Il n'importe QUE `modele`, jamais le barrel de la feature.**
 * `features/aide/index.tsx` monte les routes ; `./contenu` pèse 79 ko. Ce
 * composant-ci vit dans la coque d'administration, donc dans le lot initial de
 * tout le back-office : lui faire traverser le barrel rapatrierait la
 * documentation entière chez le guichet, chaque matin. C'est exactement le
 * défaut qui vient d'être réparé (+15,6 % sur l'entrée) — on ne le refait pas
 * par une porte voisine. `modele.ts` n'importe rien du tout.
 *
 * **2. Il s'ouvre dans un nouvel onglet.** Quelqu'un qui consulte l'aide est au
 * milieu d'un geste : une vente en cours, un bulletin ouvert, un panier qui
 * court. Le faire naviguer lui ferait perdre son travail — et le panier tient
 * quinze minutes.
 *
 * **3. Il a deux allures, et c'est la place qui décide.** En `bouton`, encadré,
 * il annonce l'aide d'un écran entier (la coque le pose une fois en haut). En
 * `ligne`, il se glisse au POINT D'USAGE — au pied de l'encadré qui refuse une
 * vente, à côté du verrou du plan, sous l'interrupteur du mode hors ligne —
 * là où la question se pose vraiment. Le `ton` sombre existe pour la seule
 * coque de contrôle d'accès, qui est noire : un lien gris ardoise y serait
 * illisible.
 */

import { urlAide } from '../features/aide/modele'

interface Props {
  /**
   * La référence visée : `aide/paiements/encaisser`, ou mieux
   * `aide/paiements/encaisser#deductions` quand une section répond exactement à
   * la question de l'endroit où le lien est posé.
   */
  readonly reference: string
  /**
   * Ce que le lien annonce. Défaut : « Aide ». Au point d'usage, on le nomme
   * par la QUESTION (« Pourquoi ce tarif manque ? ») : « Aide », répété quinze
   * fois, n'apprend au lecteur ni où il va ni pourquoi il irait.
   */
  readonly libelle?: string
  /** `bouton` : encadré, pour l'aide d'un écran. `ligne` : au point d'usage. */
  readonly variante?: 'bouton' | 'ligne'
  /** `sombre` pour les surfaces noires (la coque de contrôle d'accès). */
  readonly ton?: 'clair' | 'sombre'
}

const CLASSES_BOUTON: Readonly<Record<'clair' | 'sombre', string>> = {
  clair:
    'border-slate-200 text-slate-500 hover:border-rideau-700 hover:text-rideau-700 focus-visible:outline-rideau-700',
  sombre:
    'border-slate-600 text-slate-300 hover:border-slate-300 hover:text-white focus-visible:outline-slate-300',
}

const CLASSES_LIGNE: Readonly<Record<'clair' | 'sombre', string>> = {
  clair: 'text-rideau-700 focus-visible:outline-rideau-700',
  sombre: 'text-slate-300 hover:text-white focus-visible:outline-slate-300',
}

export function LienAide({
  reference,
  libelle = 'Aide',
  variante = 'bouton',
  ton = 'clair',
}: Props) {
  /* Le nom accessible dit OÙ l'on va et QUE l'onglet change : « Aide » seul,
     répété sur vingt écrans, ne distingue rien dans la liste des liens d'un
     lecteur d'écran, et un nouvel onglet non annoncé désoriente. */
  const nomAccessible = `${libelle} — documentation (nouvel onglet)`

  if (variante === 'ligne') {
    return (
      <a
        href={urlAide(reference)}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 ${CLASSES_LIGNE[ton]}`}
        aria-label={nomAccessible}
      >
        <span aria-hidden="true">→</span>
        {libelle}
      </a>
    )
  }

  return (
    <a
      href={urlAide(reference)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-[0.8125rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${CLASSES_BOUTON[ton]}`}
      aria-label={nomAccessible}
    >
      <span aria-hidden="true">?</span>
      {libelle}
    </a>
  )
}
